/**
 * [INPUT]: 依赖 @mediapipe/tasks-vision 的 FaceLandmarker/FilesetResolver
 * [OUTPUT]: 对外提供 getLandmarker()（懒加载 + 单例缓存 + 状态订阅）、preloadModel()、getModelStatus()、subscribeModelStatus()、disposeLandmarker()
 * [POS]: apps/web/pipeline 的 MediaPipe 加载层，模型走 public/models 过渡托管，WASM 走 CDN
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision'
import { PIPELINE_CONFIG } from './config'

let landmarkerPromise: Promise<FaceLandmarker> | null = null

export type ModelStatus = 'idle' | 'loading' | 'ready' | 'error'

let modelStatus: ModelStatus = 'idle'
const statusListeners = new Set<(status: ModelStatus) => void>()

function setModelStatus(next: ModelStatus): void {
  modelStatus = next
  for (const fn of statusListeners) fn(next)
}

/** 当前模型加载状态（供 UI 分阶段反馈：首次加载 vs 实际分析）。 */
export function getModelStatus(): ModelStatus {
  return modelStatus
}

/** 订阅模型状态变化，返回取消订阅函数。 */
export function subscribeModelStatus(fn: (status: ModelStatus) => void): () => void {
  statusListeners.add(fn)
  return () => {
    statusListeners.delete(fn)
  }
}

/** 懒加载 FaceLandmarker 单例。首评后缓存；加载状态对外订阅。失败清空缓存可重试。 */
export function getLandmarker(): Promise<FaceLandmarker> {
  if (!landmarkerPromise) {
    setModelStatus('loading')
    landmarkerPromise = createLandmarker()
      .then((lm) => {
        setModelStatus('ready')
        return lm
      })
      .catch((err) => {
        // 失败时清空缓存，使下次调用可重试——否则一次模型加载失败会永久毒化单例
        landmarkerPromise = null
        setModelStatus('error')
        throw err
      })
  }
  return landmarkerPromise
}

/**
 * 预热模型：语义上等于 getLandmarker，供落地页空闲时提前触发，把首次 WASM+模型下载
 * 藏进用户阅读落地页的时间内，从而上传后不再卡在加载。失败静默（正式评分时会再报错）。
 */
export function preloadModel(): Promise<FaceLandmarker | null> {
  return getLandmarker().catch(() => {
    // 预热失败不抛出：静默回退，留给正式 scoreImage 路径统一报错
    return null
  })
}

async function createLandmarker(): Promise<FaceLandmarker> {
  const vision = await FilesetResolver.forVisionTasks(PIPELINE_CONFIG.model.wasmUrl)
  return FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: PIPELINE_CONFIG.model.landmarkerUrl,
      delegate: 'GPU',
    },
    runningMode: 'IMAGE',
    numFaces: PIPELINE_CONFIG.face.maxFaces,
    outputFacialTransformationMatrixes: true,
    outputFaceBlendshapes: false,
  })
}

export function disposeLandmarker(): void {
  if (landmarkerPromise) {
    landmarkerPromise.then((lm) => lm.close()).catch(() => {})
  }
  landmarkerPromise = null
}